import { useCallback, useMemo, useRef, useEffect } from 'react'
import { Autocomplete, Box, Paper, TextField, Typography } from '@mui/material'
import { useMediaQuery, useTheme } from '@mui/material'
import { Add, ErrorOutline, WarningAmber } from '@mui/icons-material'
import { calcTemplateWarning } from '../utils/calc-template-warning'
import { TpChip } from '~/components/tp-chip'
import { TpTooltip } from '~/components'
import { useCropModalContext, useCropModalContextDispatch, setSelectedCollections, setSpeciesTouched } from '../context/crop-modal-context'
import { DESKTOP_BREAKPOINT } from '~/common/constants/layout-constants'
import { useFeatures } from '~/common/use-features'

export const CropSpeciesField = ({ cropArchetypes, sortedCropTemplates, filterSpeciesOptions, collectionTypes, onSpeciesChange, onCustomCropToggle, frostDates, farmClimate }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))
  const speciesInputRef = useRef(null)

  const warningMap = useMemo(() => {
    if (!frostDates?.ffd) return {}
    const map = {}
    for (const t of (sortedCropTemplates || [])) {
      const archetype = cropArchetypes.find(a => a.code === t.archetype_code)
      if (archetype) map[t.id] = calcTemplateWarning(t, archetype, frostDates, collectionTypes, farmClimate)
    }
    return map
  }, [sortedCropTemplates, cropArchetypes, frostDates, collectionTypes, farmClimate])

  const { species, selectedCollections, speciesTouched, cropId, open } = useCropModalContext()
  const dispatch = useCropModalContextDispatch()
  const { features } = useFeatures()

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (speciesInputRef.current) speciesInputRef.current.focus()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [open])

  const handleAddCollection = useCallback((code) => {
    dispatch(setSelectedCollections({ selectedCollections: [...selectedCollections, code] }))
  }, [dispatch, selectedCollections])

  const handleRemoveCollection = useCallback((code) => {
    dispatch(setSelectedCollections({ selectedCollections: selectedCollections.filter(c => c !== code) }))
    onSpeciesChange(null, null)
  }, [dispatch, selectedCollections, onSpeciesChange])

  const CollectionFilterPaper = useCallback(({ children, ...props }) => (
    <Paper {...props}>
      {features['crop_template_collections'] && (
        <Box
          onMouseDown={(e) => e.preventDefault()}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            p: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
          }}
        >
          {collectionTypes.map((ct) => {
            const isSelected = selectedCollections.includes(ct.code)
            return (
              <TpTooltip key={ct.code} title={ct.description} enterDelay={500} followCursor>
                <TpChip
                  isSelected={isSelected}
                  label={ct.label}
                  onClick={() => { if (!isSelected) handleAddCollection(ct.code) }}
                  onDelete={isSelected ? () => handleRemoveCollection(ct.code) : undefined}
                />
              </TpTooltip>
            )
          })}
        </Box>
      )}
      {children}
      <Box
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onCustomCropToggle?.(true)}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          p: 1,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Add fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary">Custom Species...</Typography>
      </Box>
    </Paper>
  ), [collectionTypes, selectedCollections, handleAddCollection, handleRemoveCollection, onCustomCropToggle, features])

  return (
    <TpTooltip title={cropId ? 'Can\'t edit the crop species, create a new crop instead' : ''}>
      <Autocomplete
        size={isDesktop ? 'medium' : 'small'}
        filterOptions={filterSpeciesOptions}
        disabled={cropId}
        disablePortal
        options={sortedCropTemplates || []}
        groupBy={(option) => cropArchetypes.find(a => a.code === option.archetype_code)?.label}
        getOptionLabel={(option) => option.name}
        value={species}
        onBlur={() => dispatch(setSpeciesTouched({ speciesTouched: true }))}
        slots={{ paper: CollectionFilterPaper }}
        renderOption={({ key, ...props }, option) => {
          const warning = warningMap[option.id]
          return (
            <li key={key} {...props}>
              <Box sx={{ flex: 1 }}>{option.name}</Box>
              {warning && (
                <TpTooltip title={warning.message}>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    {warning.severity === 'error'
                      ? <ErrorOutline fontSize="small" color="error" />
                      : <WarningAmber fontSize="small" sx={{ color: 'warning.main' }} />
                    }
                  </Box>
                </TpTooltip>
              )}
            </li>
          )
        }}
        renderGroup={(params) => (
          <li key={params.key}>
            <Box
              component="div"
              sx={{
                color: 'primary.dark',
                fontWeight: 600,
                position: 'sticky',
                top: '-8px',
                padding: '4px 10px',
                backgroundColor: 'background.paper',
              }}
            >
              {params.group}
            </Box>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={speciesInputRef}
            label="Crop Species"
            required
            error={!species && speciesTouched}
          />
        )}
        onChange={onSpeciesChange}
      />
    </TpTooltip>
  )
}
